import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { Player, Position, GameState, TeamInLeague, TacticalPosition, League } from '../../domain/entities';
import { Modal } from './Modal';
import { calculateStarRating, FORMATIONS, POSITION_MAP, TACTICAL_ATTRIBUTES } from '../../domain/constants';
import { PlayerFace } from './PlayerFace';
import './PlayerComparisonModal.css';
import { RatingBadge } from './RatingBadge';
import { PotentialBadge } from './PotentialBadge';
import { FitnessBadge } from './FitnessBadge';
import { MoraleBadge } from './MoraleBadge';
import { calculateMatchPerformance, calculatePositionalPenalty, formatValue } from '../../utils/utilities';
import { AiService } from '../../services/AiService';
import { soundService } from '../../services/SoundService';
import { MatchPerformanceDetailModal } from './MatchPerformanceDetailModal';

interface PlayerComparisonModalProps {
    isOpen: boolean;
    onClose: () => void;
    playerA_initial: Player;
    playerTeam: TeamInLeague;
    gameState: GameState;
    isAiPunditEnabled: boolean;
}

const generateOfflineSummary = (playerA: Player, playerB: Player, position: TacticalPosition | Position): string => {
    const weights = TACTICAL_ATTRIBUTES[position];
    if (!weights) {
        return "Cannot generate a detailed comparison for this general position. Please select a specific tactical role.";
    }

    const allRelevantAttrs = [...weights.key, ...weights.secondary];
    if (allRelevantAttrs.length === 0) {
         return "No key attributes defined for this position, cannot generate a meaningful comparison.";
    }

    let scoreA = 0;
    let scoreB = 0;
    const diffs: { attr: string, diff: number }[] = [];

    allRelevantAttrs.forEach(attr => {
        const valA = playerA.attributes[attr] || 0;
        const valB = playerB.attributes[attr] || 0;
        const isKey = weights.key.includes(attr);
        
        scoreA += valA * (isKey ? 1.5 : 1);
        scoreB += valB * (isKey ? 1.5 : 1);

        if (Math.abs(valA - valB) >= 5) {
            diffs.push({ attr, diff: valA - valB });
        }
    });

    diffs.sort((a, b) => Math.abs(b.diff) - Math.abs(a.diff));

    const scoreDifference = Math.abs(scoreA - scoreB);

    if (scoreDifference < (allRelevantAttrs.length * 1.5)) {
        const advantagePlayerName = scoreA > scoreB ? playerA.name : playerB.name;
        if (diffs.length > 0) {
            const biggestDiff = diffs[0];
            const advantageText = (biggestDiff.diff > 0 ? playerA.name : playerB.name) + ` in ${biggestDiff.attr}`;
            return `Both players are very evenly matched for the ${position} role. ${advantagePlayerName} has a slight edge overall, but the most significant difference is the advantage for ${advantageText}. This could be the deciding factor depending on your tactical needs.`;
        }
        return `Both players are almost identical in suitability for the ${position} role. This choice comes down to other factors like age, potential, or current form.`;
    }

    const betterPlayer = scoreA > scoreB ? playerA : playerB;
    const worsePlayer = scoreA > scoreB ? playerB : playerA;

    const betterPlayerDiffs = diffs.filter(d => (scoreA > scoreB && d.diff > 0) || (scoreB > scoreA && d.diff < 0));
    const worsePlayerDiffs = diffs.filter(d => (scoreA < scoreB && d.diff > 0) || (scoreB > scoreA && d.diff < 0));

    let summary = `For the ${position} position, ${betterPlayer.name} appears to be the stronger option. `;
    
    if (betterPlayerDiffs.length > 0) {
        const significantStrengths = betterPlayerDiffs.slice(0, 2).map(d => `${d.attr} (+${Math.round(Math.abs(d.diff))})`).join(' and ');
        summary += `They hold a significant advantage in key areas like ${significantStrengths}. `;
    } else {
        summary += `They have a more well-rounded attribute profile for this role. `;
    }

    if (worsePlayerDiffs.length > 0) {
        const counterStrengths = worsePlayerDiffs.slice(0, 1).map(d => `${d.attr} (+${Math.round(Math.abs(d.diff))})`).join('');
        summary += `While ${worsePlayer.name} is better in terms of ${counterStrengths}, it is likely not enough to compensate for deficiencies in other role-specific attributes. `;
    }

    summary += `Overall, ${betterPlayer.name}'s skillset makes them a better immediate fit for your team in this position.`;

    return summary;
};


const positionOrder: Record<Position, number> = { GK: 1, DEF: 2, MID: 3, FWD: 4 };

const allPhysicalAttributes = ['pace'];
const allTechnicalAttributes = ['handling', 'tackling', 'marking', 'heading', 'passing', 'dribbling', 'finishing', 'creativity'];
const allMentalAttributes = ['positioning'];

export const PlayerComparisonModal: React.FC<PlayerComparisonModalProps> = ({ isOpen, onClose, playerA_initial, playerTeam, gameState, isAiPunditEnabled }) => {
    const [playerA, setPlayerA] = useState<Player | null>(null);
    const [playerB, setPlayerB] = useState<Player | null>(null);
    const [selectingFor, setSelectingFor] = useState<'A' | 'B' | null>(null);

    const [positionToCompare, setPositionToCompare] = useState<Position>('GK');
    const [tacticalPositionToCompare, setTacticalPositionToCompare] = useState<TacticalPosition | 'none'>('none');

    const [aiSummary, setAiSummary] = useState<string | null>(null);
    const [isAiLoading, setIsAiLoading] = useState(false);
    
    const [performanceModalData, setPerformanceModalData] = useState<{ player: Player, tacticalPosition: TacticalPosition } | null>(null);

    useEffect(() => {
        if (isOpen) {
            setPlayerA(playerA_initial);
            setPlayerB(null);
            setPositionToCompare(playerA_initial.position);
            setTacticalPositionToCompare('none');
            setAiSummary(null);
            setIsAiLoading(false);
        }
    }, [isOpen, playerA_initial]);

    const tacticalPositionOptions = useMemo(() => {
        const formationPositions = FORMATIONS[playerTeam.tactics.formation] || [];
        return formationPositions.filter(pos => POSITION_MAP[pos] === positionToCompare);
    }, [positionToCompare, playerTeam.tactics.formation]);

    const getPlayerTeamColor = (player: Player): string | undefined => {
        for (const league of Object.values(gameState.leagues) as League[]) {
            for (const team of league.teams) {
                if (team.squad.some(p => p.id === player.id)) {
                    return team.colors.primary;
                }
            }
        }
        return undefined;
    };

    useEffect(() => {
        setTacticalPositionToCompare('none');
    }, [positionToCompare]);

    const { keyAttributes, secondaryAttributes, otherAttributes } = useMemo(() => {
        const pos = tacticalPositionToCompare !== 'none' ? tacticalPositionToCompare : positionToCompare;
        const weights = TACTICAL_ATTRIBUTES[pos];

        if (!weights) {
            const allAttrs = [...allPhysicalAttributes, ...allTechnicalAttributes, ...allMentalAttributes];
            return { keyAttributes: [], secondaryAttributes: [], otherAttributes: allAttrs };
        }

        const other = [...allPhysicalAttributes, ...allTechnicalAttributes, ...allMentalAttributes]
            .filter(attr => !weights.key.includes(attr) && !weights.secondary.includes(attr));
        
        return { keyAttributes: weights.key, secondaryAttributes: weights.secondary, otherAttributes: other };
    }, [positionToCompare, tacticalPositionToCompare]);

    useEffect(() => {
        if (!playerA || !playerB) {
            setAiSummary(null);
            return;
        }

        let isCancelled = false;
        const getSummary = async () => {
            setIsAiLoading(true);
            setAiSummary(null);
            const position = tacticalPositionToCompare !== 'none' ? tacticalPositionToCompare : positionToCompare;

            try {
                if (isAiPunditEnabled && AiService.isAiAvailable()) {
                    const summary = await AiService.generatePlayerComparison(playerA, playerB, position);
                    if (!isCancelled) setAiSummary(summary);
                } else {
                    const summary = generateOfflineSummary(playerA, playerB, position);
                    if (!isCancelled) setAiSummary(summary);
                }
            } catch (e) {
                console.error("AI comparison failed, using fallback:", e);
                const summary = generateOfflineSummary(playerA, playerB, position);
                if (!isCancelled) setAiSummary(summary);
            } finally {
                if (!isCancelled) setIsAiLoading(false);
            }
        };

        getSummary();
        return () => { isCancelled = true; };
    }, [playerA, playerB, positionToCompare, tacticalPositionToCompare, isAiPunditEnabled]);

    const handleSelectPlayer = (player: Player) => {
        soundService.playClick();
        if (selectingFor === 'A') {
            setPlayerA(player);
        } else {
            setPlayerB(player);
        }
        setSelectingFor(null);
    };

    // Normalize 0-100 scale to 0-20 scale
    const normalizeToScale = (value: number): number => Math.round((value / 100) * 20 * 10) / 10;

    const renderAttributeRow = (attr: string, playerA: Player, playerB: Player) => {
        const rawValA = Math.round(playerA.attributes[attr] || 0);
        const rawValB = Math.round(playerB.attributes[attr] || 0);
        const valA = normalizeToScale(rawValA);
        const valB = normalizeToScale(rawValB);
        const diff = rawValA - rawValB;

        const barAColor = diff > 0 ? 'var(--positive-color)' : 'var(--highlight-color)';
        const barBColor = diff < 0 ? 'var(--positive-color)' : 'var(--highlight-color)';

        return (
            <div className="attribute-row" key={attr}>
                <div className="attribute-bar-wrapper left">
                    <span className="attribute-value">{valA}/20</span>
                    <div className="attribute-bar-container">
                        <div className="attribute-bar-fill" style={{ width: `${rawValA}%`, backgroundColor: barAColor, float: 'right' }}></div>
                    </div>
                </div>
                <span className="attribute-name">{attr}</span>
                <div className="attribute-bar-wrapper right">
                    <div className="attribute-bar-container">
                        <div className="attribute-bar-fill" style={{ width: `${rawValB}%`, backgroundColor: barBColor }}></div>
                    </div>
                    <span className="attribute-value">{valB}/20</span>
                </div>
            </div>
        );
    };

    const PlayerBox = ({ player, onClick }: { player: Player | null, onClick: () => void }) => {
        if (!player) {
            return (
                <div className="player-box placeholder" onClick={onClick}>
                    <span>+</span>
                    <p>Select Player</p>
                </div>
            );
        }

        const isPlayerTeamPlayer = !!playerTeam.squad.find(p => p.id === player.id);
        const leagueName = isPlayerTeamPlayer 
            ? gameState.playerLeagueName 
            // FIX: Correctly find the league for a player from another team.
            : (Object.entries(gameState.leagues).find(([name, league]) => (league as League).teams.some(t => t.squad.some(p => p.id === player.id)))?.[0] || '');
        
        const leagueAverage = useMemo(() => {
            if (!leagueName) return 50;
            // FIX: Add a guard to prevent crash if league is not found, and cast to League to satisfy TypeScript.
            const league = gameState.leagues[leagueName] as League | undefined;
            if (!league) return 50;
            const allPlayers = league.teams.flatMap(t => t.squad);
            if (allPlayers.length === 0) return 50;
            return allPlayers.reduce((sum, p) => sum + p.skill, 0) / allPlayers.length;
        }, [leagueName, gameState.leagues]);

        const rating = calculateStarRating(player.skill, leagueAverage);
        
        const { basePerf, penaltyPercent } = useMemo(() => {
            if (!player || tacticalPositionToCompare === 'none') {
                return { basePerf: null, penaltyPercent: null };
            }
            const perf = calculateMatchPerformance(player);
            const penalty = calculatePositionalPenalty(player, tacticalPositionToCompare);
            return { basePerf: Math.round(perf), penaltyPercent: Math.round(penalty * 100) };
        }, [player, tacticalPositionToCompare]);

        const handlePerfClick = (e: React.MouseEvent) => {
            e.stopPropagation();
            if (player && tacticalPositionToCompare !== 'none') {
                setPerformanceModalData({ player, tacticalPosition: tacticalPositionToCompare });
            }
        };

        return (
            <div className="player-box">
                <div className="player-box-header">
                    <PlayerFace player={player} teamColor={getPlayerTeamColor(player)} />
                    <div className="player-box-info">
                        <h3>{player.name}</h3>
                        <p>{player.position} / AGE: {player.age}</p>
                    </div>
                </div>
                <div className="player-box-stats">
                    {basePerf !== null ? (
                         <button className="stat-item clickable" onClick={handlePerfClick} title="Click for performance breakdown">
                            <span>
                                {basePerf}
                                {penaltyPercent > 0 && <span className="perf-mod negative">-{penaltyPercent}%</span>}
                            </span>
                            <label>Match Perf</label>
                        </button>
                    ) : (
                        <div className="stat-item">
                            <span>{player.skill}</span>
                            <label>Skill</label>
                        </div>
                    )}
                    <div className="stat-item">
                        <RatingBadge rating={rating} leagueName={leagueName} />
                        <label>Rating</label>
                    </div>
                </div>
                <div className="player-box-details">
                    <div className="detail-row">
                        <span className="detail-label">Potential:</span>
                        <PotentialBadge potential={player.potential} />
                    </div>
                    <div className="detail-row">
                        <span className="detail-label">Fitness:</span>
                        <FitnessBadge fitness={player.fitness} />
                    </div>
                    <div className="detail-row">
                        <span className="detail-label">Morale:</span>
                        <MoraleBadge morale={player.morale} />
                    </div>
                    <div className="detail-row">
                        <span className="detail-label">Form:</span>
                        <span className={player.form > 0 ? 'positive' : player.form < 0 ? 'negative' : 'text-secondary'}>
                            {player.form > 0 ? '+' : ''}{player.form}
                        </span>
                    </div>
                    <div className="detail-row">
                        <span className="detail-label">Value:</span>
                        <span className="detail-value">£{player.marketValue.toLocaleString()}</span>
                    </div>
                    {player.contractExpiresInWeek && (
                        <div className="detail-row">
                            <span className="detail-label">Contract:</span>
                            <span className="detail-value">{Math.ceil(player.contractExpiresInWeek / 52)} yr{Math.ceil(player.contractExpiresInWeek / 52) !== 1 ? 's' : ''}</span>
                        </div>
                    )}
                </div>
                <button className="button small" onClick={onClick}>Change Player</button>
            </div>
        );
    };
    
    const PlayerSelectionList = ({ onSelect, onClose, players, currentPlayerId }: { onSelect: (p: Player) => void, onClose: () => void, players: Player[], currentPlayerId?: number | null }) => {
        const [search, setSearch] = useState('');
        const filteredPlayers = useMemo(() => players
            .filter(p => p.id !== currentPlayerId)
            .filter(p => p.name.toLowerCase().includes(search.toLowerCase()))
            .sort((a, b) => {
                if (positionOrder[a.position] !== positionOrder[b.position]) {
                    return positionOrder[a.position] - positionOrder[b.position];
                }
                return b.skill - a.skill; // secondary sort by skill
            }), 
        [players, search, currentPlayerId]);

        return (
            <Modal isOpen={true} onClose={onClose} className="player-selection-modal">
                <h3>Select a Player</h3>
                <input type="text" placeholder="Search squad..." value={search} onChange={e => setSearch(e.target.value)} />
                <div className="player-list-container">
                    {filteredPlayers.map(p => (
                        <div key={p.id} className="player-list-item" onClick={() => onSelect(p)}>
                             <PlayerFace player={p} teamColor={getPlayerTeamColor(p)} />
                             <div className="player-list-info">
                                <strong>{p.name}</strong>
                                <span>{p.position} / SKL: {p.skill}</span>
                             </div>
                        </div>
                    ))}
                </div>
            </Modal>
        );
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} className="player-comparison-modal">
            {selectingFor && <PlayerSelectionList onSelect={handleSelectPlayer} onClose={() => setSelectingFor(null)} players={playerTeam.squad} currentPlayerId={selectingFor === 'A' ? playerB?.id : playerA?.id} />}
            <h2>Compare Players</h2>

            <div className="comparison-header">
                <div className="form-group">
                    <label>Compare for Position:</label>
                    <div className="select-group">
                        <select value={positionToCompare} onChange={e => setPositionToCompare(e.target.value as Position)}>
                            <option value="GK">Goalkeeper</option>
                            <option value="DEF">Defender</option>
                            <option value="MID">Midfielder</option>
                            <option value="FWD">Forward</option>
                        </select>
                        {tacticalPositionOptions.length > 0 && (
                            <select value={tacticalPositionToCompare} onChange={e => setTacticalPositionToCompare(e.target.value as TacticalPosition)}>
                                <option value="none">Overall ({positionToCompare})</option>
                                {tacticalPositionOptions.map(pos => <option key={pos} value={pos}>{pos}</option>)}
                            </select>
                        )}
                    </div>
                </div>
                <p className="hint"><span className="icon">💡</span> Attributes are weighted by position importance. Switch positions to see how players compare in different roles.</p>
            </div>

            <div className="comparison-body">
                <PlayerBox player={playerA} onClick={() => setSelectingFor('A')} />
                <div className="vs-separator">VS</div>
                <PlayerBox player={playerB} onClick={() => setSelectingFor('B')} />
            </div>

            {playerA && playerB && (
                <div className="comparison-attributes-section">
                    {/* Column headers with player names */}
                    <div className="attribute-column-headers">
                        <div className="header-left">{playerA.name}</div>
                        <div className="header-center">Attribute</div>
                        <div className="header-right">{playerB.name}</div>
                    </div>

                    <div className="attribute-group">
                        <h3>⭐ Key Attributes</h3>
                        {keyAttributes.map(attr => renderAttributeRow(attr, playerA, playerB))}
                    </div>
                    {secondaryAttributes.length > 0 && (
                        <div className="attribute-group">
                            <h3>Secondary Attributes</h3>
                            {secondaryAttributes.map(attr => renderAttributeRow(attr, playerA, playerB))}
                        </div>
                    )}
                     <div className="attribute-group other">
                        <h3>Other Attributes</h3>
                        {otherAttributes.map(attr => renderAttributeRow(attr, playerA, playerB))}
                    </div>
                </div>
            )}
            
            {playerA && playerB && (
                 <div className="comparison-summary-section">
                    <h3>⚡ Scout's Report</h3>
                    <div className="summary-box">
                        {isAiLoading ? "Scout is thinking..." : (aiSummary || "Could not generate summary.")}
                    </div>
                </div>
            )}
            
            {performanceModalData && (
                <MatchPerformanceDetailModal
                    isOpen={!!performanceModalData}
                    onClose={() => setPerformanceModalData(null)}
                    player={performanceModalData.player}
                    tacticalPosition={performanceModalData.tacticalPosition}
                />
            )}

            <div className="modal-actions">
                <button className="button" onClick={onClose}>Close</button>
            </div>
        </Modal>
    );
};
